import { ReactNode, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

export type Anchors = 'topCenter' | 'bottomCenter' | 'bestCenter';

const Y_PADDING = 4;
const COLLISION_MARGIN = 8;

type PopupOverlayProps = {
    map: google.maps.Map | null;
    children: ReactNode;
    position: google.maps.LatLng | google.maps.LatLngLiteral;
    anchor?: Anchors;
    padding?: number;
    // Add a reference to the corresponding marker element
    markerElement?: HTMLElement | null;
};

const PopupOverlay = ({
    map,
    children,
    position,
    anchor = 'topCenter',
    padding = Y_PADDING,
    markerElement = null,
}: PopupOverlayProps) => {
    const root = useRef(document.createElement('div'));
    const overlayRef = useRef<google.maps.OverlayView>();
    const currentAnchor = useRef<Anchors>(anchor);
    const currentPosX = useRef<number>(0);

    const updatePosition = useCallback(() => {
        if (!overlayRef.current || !map) return;
        const overlayProjection = overlayRef.current.getProjection();
        if (!overlayProjection) return;

        const pos = overlayProjection.fromLatLngToDivPixel(
            new google.maps.LatLng(position)
        );
        if (!pos) return;

        // Get map container bounds for collision detection
        const mapDiv = map.getDiv();
        const mapBounds = mapDiv.getBoundingClientRect();

        // Measure the popover dimensions
        const popoverRect = root.current.getBoundingClientRect();
        const popoverHeight = popoverRect.height || 200;

        // Get marker height directly from the element
        // AdvancedMarkerElement anchors at BOTTOM center, so marker spans from (pos.y - markerHeight) to pos.y
        let markerHeight = 20; // default fallback
        if (markerElement) {
            markerHeight = markerElement.offsetHeight || 20;
        }

        // Calculate which anchor works best with collision avoidance
        let chosenAnchor = anchor;
        if (anchor === 'bestCenter') {
            // Space above = from top of map to top of marker
            const spaceAbove = pos.y - markerHeight;
            // Space below = from bottom of marker to bottom of map
            const spaceBelow = mapBounds.height - pos.y;

            if (spaceAbove >= popoverHeight + padding + COLLISION_MARGIN) {
                chosenAnchor = 'topCenter';
            } else if (
                spaceBelow >=
                popoverHeight + padding + COLLISION_MARGIN
            ) {
                chosenAnchor = 'bottomCenter';
            } else {
                chosenAnchor =
                    spaceAbove > spaceBelow ? 'topCenter' : 'bottomCenter';
            }
        }
        currentAnchor.current = chosenAnchor;

        // Calculate Y position
        // For topCenter: popover bottom should be [padding]px above marker top
        //   - Marker top is at pos.y - markerHeight
        //   - With translateY(-100%), we set top to where we want the bottom
        // For bottomCenter: popover top should be [padding]px below marker bottom
        //   - Marker bottom is at pos.y
        let topPosition: number;
        if (chosenAnchor === 'topCenter') {
            topPosition = pos.y - markerHeight - padding;
        } else {
            topPosition = pos.y + padding;
        }

        // Store current X position for collision detection
        currentPosX.current = pos.x;

        // Set position - center popover on marker's X position
        root.current.style.left = `${pos.x}px`;
        root.current.style.top = `${topPosition}px`;
        root.current.style.transform = `translateX(-50%) ${
            chosenAnchor === 'topCenter' ? 'translateY(-100%)' : ''
        }`;
    }, [map, position, anchor, padding, markerElement]);

    // Separate effect for edge collision detection - runs after render
    useEffect(() => {
        if (!map) return;

        const adjustForCollision = () => {
            const popoverRect = root.current.getBoundingClientRect();
            const mapRect = map.getDiv().getBoundingClientRect();

            if (popoverRect.width === 0) return;

            let adjustment = 0;

            // Check if popover extends past left edge
            if (popoverRect.left < mapRect.left + COLLISION_MARGIN) {
                adjustment = mapRect.left + COLLISION_MARGIN - popoverRect.left;
            }
            // Check if popover extends past right edge
            else if (popoverRect.right > mapRect.right - COLLISION_MARGIN) {
                adjustment =
                    mapRect.right - COLLISION_MARGIN - popoverRect.right;
            }

            if (adjustment !== 0) {
                root.current.style.left = `${
                    currentPosX.current + adjustment
                }px`;
            }
        };

        // Run after a microtask to ensure the popover has rendered
        const timeoutId = setTimeout(adjustForCollision, 10);
        return () => clearTimeout(timeoutId);
    }, [map, position]);

    useEffect(() => {
        if (!map || !window.google) return;

        root.current.style.position = 'absolute';
        root.current.style.willChange = 'transform';

        // Prevent clicks inside the popover from propagating to the
        // map / feature-layer so they don't inadvertently close the popover.
        const el = root.current;
        const stopProp = (e: MouseEvent) => e.stopPropagation();
        el.addEventListener('click', stopProp);

        overlayRef.current = new google.maps.OverlayView();

        overlayRef.current.onAdd = function () {
            const panes = this.getPanes();
            panes?.floatPane.appendChild(root.current);
        };

        overlayRef.current.draw = updatePosition;

        overlayRef.current.onRemove = function () {
            if (root.current.parentElement) {
                root.current.parentElement.removeChild(root.current);
            }
        };

        overlayRef.current.setMap(map);

        const listener = map.addListener('bounds_changed', () => {
            updatePosition();
        });

        return () => {
            el.removeEventListener('click', stopProp);
            overlayRef.current?.setMap(null);
            google.maps.event.removeListener(listener);
        };
    }, [map, updatePosition]);

    return createPortal(children, root.current);
};

export default PopupOverlay;
